import { Dialog } from '@headlessui/react'
import { useForm, useStore } from '@tanstack/react-form'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import debug from 'debug'
import { useMemo, useState } from 'react'
import Button from 'src/components/buttons/button'
import Drawer from 'src/components/containers/drawer'
import Form from 'src/components/fields/form'
import RadioGroupField from 'src/components/fields/radio-group.field'
import TextField from 'src/components/fields/text.field'
import Tooltip from 'src/components/messages/tooltip'
import inputValidation from 'src/utils/form/input.validation'
import { isRequiredOnBlur } from 'src/utils/form/validation'
import useControlPlaneApi from 'src/utils/hooks/use-control-plane-api'
import { refetchOrg, useGetOrg } from 'src/utils/hooks/use-get-org'
import { refetchOrgDetails } from 'src/utils/hooks/use-get-org-details'
import notify from 'src/utils/notify'
import analytics, { EVENTS } from 'src/utils/analytics'

import { CustomRoleBuilder } from './roles/custom-role-builder'
import { ReadOnlyRoleOverview } from './roles/read-only-role-overview'
import { RoleTemplateRadioGroup } from './roles/role-template-radio-group'
import { CUSTOM_ROLE_TEMPLATE_NAME, DEFAULT_ROLE_TEMPLATE_NAME, getRoleTemplates } from './roles/role-templates'
import { validateCapabilities } from './roles/validation-utils'
import { InviteToOrgDocument, CapabilitiesInput } from '@vendia/control-plane-api'

export const logger = debug('app:OrgInvite')

interface InviteResponse {
  inviteToOrg: {
    expiration: string
  }
  errors: {
    message: string
  }[]
}

const OrgInvite = ({ disabled = false }: { disabled?: boolean }) => {
  const getOrg = useGetOrg()
  const org = getOrg?.data?.getOrganization
  const hasNoApprovedDomains = org?.domains?.length === 0
  const orgDomains = org?.domains?.join(', ')
  const queryClient = useQueryClient()
  const client = useControlPlaneApi()
  const [showInvite, setShowInvite] = useState(false)
  const inviteMutation = useMutation({
    mutationFn: ({
      inviteeEmail,
      roleTemplateName,
      customRoleCapabilities,
    }: {
      inviteeEmail: string
      invitePermissionsMode: 'default' | 'custom'
      roleTemplateName: string
      customRoleCapabilities: string
    }) => {
      logger('SUBMIT', inviteeEmail, roleTemplateName)
      // Use server default role if the user selected the default role
      // @ts-ignore - accessing variable from closure
      if (invitePermissionsMode === 'default') {
        return client.graphqlFetch({
          query: InviteToOrgDocument,
          variables: { userId: inviteeEmail, capabilities: null },
        })
      }
      // @ts-ignore - accessing variable from closure
      if (invitePermissionsMode === 'custom') {
        return client.graphqlFetch({
          query: InviteToOrgDocument,
          variables: {
            userId: inviteeEmail,
            capabilities: JSON.parse(customRoleCapabilities),
          },
        })
      }
      // The preview role templates don't have the user's email address in the NameResources
      // Reconstruct now that we have it and match by name...
      const templatesWithInviteesEmail = getRoleTemplates({
        orgId: org!.orgId,
        orgDomains: org!.domains as string[],
      })
      const capabilities = templatesWithInviteesEmail.find((t) => t.name === roleTemplateName)?.capabilities
      return client.graphqlFetch({
        query: InviteToOrgDocument,
        variables: { userId: inviteeEmail, capabilities: capabilities as CapabilitiesInput[] },
      })
    },
  })

  const roleTemplates = useMemo(() => {
    if (!org?.orgId || !org?.domains) return []
    return getRoleTemplates({ orgId: org?.orgId, orgDomains: org?.domains })
  }, [org?.orgId, org?.domains])

  const form = useForm({
    defaultValues: {
      inviteeEmail: '',
      roleTemplateName: DEFAULT_ROLE_TEMPLATE_NAME,
      invitePermissionsMode: 'default' as 'default' | 'custom',
      customRoleCapabilities: JSON.stringify(
        [
          {
            action: 'UNI_GET',
            resources: [`UniResource(*.*.${org?.domains?.[0]})`],
          },
        ],
        null,
        2,
      ),
    },
    onSubmit: async ({ value }) => {
      logger('submit')
      if (invitePermissionsMode === 'custom') {
        if (selectedRoleTemplate?.name === CUSTOM_ROLE_TEMPLATE_NAME) {
          try {
            validateCapabilities(value.customRoleCapabilities)
          } catch (e: any) {
            console.warn('Unable to submit Org Invite', e)
            notify.error(e.message)
            return
          }
        }
      }

      try {
        const response = await inviteMutation.mutateAsync(value)
        if (response.errors) {
          analytics.track(EVENTS.ORG_MEMBER_INVITE_FAILED, {
            orgId: org?.orgId,
            inviteeEmailDomain: value.inviteeEmail.split('@')[1],
            invitePermissionsMode: invitePermissionsMode,
            roleTemplateName: value.roleTemplateName,
            errorMessage: response.errors[0]!.message,
          })
          notify.error(response.errors[0]!.message)
          return
        }

        analytics.track(EVENTS.ORG_MEMBER_INVITE_SENT, {
          orgId: org?.orgId,
          inviteeEmailDomain: value.inviteeEmail.split('@')[1],
          invitePermissionsMode: invitePermissionsMode,
          roleTemplateName: value.roleTemplateName,
        })

        close()
        refetchOrg({ queryClient })
        refetchOrgDetails({ queryClient })
      } catch (e: any) {
        console.error('Unable to invite to org', e)
        analytics.track(EVENTS.ORG_MEMBER_INVITE_FAILED, {
          orgId: org?.orgId,
          inviteeEmailDomain: value.inviteeEmail.split('@')[1],
          invitePermissionsMode: invitePermissionsMode,
          roleTemplateName: value.roleTemplateName,
          errorMessage: e.message || 'Unknown error',
        })
        notify.error('Oops, something went wrong. Please try again or contact support.')
        close()
      }
    },
  })
  const [selectedRoleTemplateName, invitePermissionsMode] = useStore(form.store, (state) => [
    state.values.roleTemplateName,
    state.values.invitePermissionsMode,
  ])
  const selectedRoleTemplate = roleTemplates.find((t) => t.name === selectedRoleTemplateName)

  const close = () => {
    logger('close')
    setShowInvite(false)
  }

  const open = () => {
    logger('open')
    form.reset()
    setShowInvite(true)
  }

  const permissionOptions = [
    {
      value: 'default',
      label: 'Default organization role',
      description: 'User will get the default role for your organization',
    },
    {
      value: 'custom',
      label: 'Custom access level',
      description: 'User will get a new role with custom permissions',
    },
  ]

  return (
    <>
      <Button kind='secondary' icon='add' iconSize={16} onClick={open} disabled={disabled || hasNoApprovedDomains}>
        Invite org member
      </Button>
      {hasNoApprovedDomains && !disabled && (
        <Tooltip className='ml-2'>
          You&rsquo;ll be able to invite members to your Organization once you have an approved domain.
        </Tooltip>
      )}
      <Drawer open={showInvite === true} onClose={() => close()} className='w-screen max-w-7xl'>
        <Form form={form}>
          <div className='grid h-screen grid-rows-[auto_minmax(auto,1fr)_auto]'>
            <header className='border-neutral-3 bg-neutral-0 flex items-center gap-4 border-b p-6'>
              <Dialog.Title as='span' className='text-lg font-medium leading-6 text-gray-900'>
                <h2 className='h3 m-0'>Invite to organization</h2>
              </Dialog.Title>
            </header>
            <aside className='flex min-h-0 overflow-y-auto'>
              <div className='border-neutral-3 flex min-h-0 flex-1 flex-col gap-8 overflow-y-auto border-r p-8'>
                <div className='!mb-0'>
                  <TextField
                    label='New member email'
                    description='Email domain must match one of your Orgs owned domains'
                    name='inviteeEmail'
                    id='inviteeEmail'
                    form={form}
                    type='email'
                    validators={{
                      onBlur: ({ value }: any) => {
                        if (!value) {
                          return 'This field is required.'
                        }
                        if (!inputValidation.isEmail.pattern.test(value)) {
                          return inputValidation.isEmail.message
                        }
                        const emailDomain = value.split('@')[1]
                        if (!org?.domains?.includes(emailDomain)) {
                          return `Email domain must match one of your Orgs owned domains (i.e., ${orgDomains}).`
                        }
                      },
                    }}
                  />
                </div>

                <RadioGroupField
                  name='invitePermissionsMode'
                  label='New user role'
                  description='Select the role this user will have'
                  form={form}
                  options={permissionOptions}
                  validators={isRequiredOnBlur}
                />

                {invitePermissionsMode === 'custom' && (
                  <form.Field name='roleTemplateName'>
                    {(field) => (
                      <RoleTemplateRadioGroup
                        label='Access level'
                        labelDescription={`Select an option below. Leave "Default Access" selected if you're not sure.`}
                        name='roleTemplateName'
                        field={field as any}
                        // Filtering out custom editor in this flow for now - too complex to be a good fit here and probably not used
                        // Also simplifies the changes needed for the new role editor UI to remove this
                        // I actually think we should just apply the org's default role on invite and remove the custom role stuff entirely!
                        hideCustomRoleOption
                      />
                    )}
                  </form.Field>
                )}
              </div>
              <div className='hidden min-h-0 flex-1 flex-col gap-8 overflow-y-auto p-8 sm:flex'>
                {invitePermissionsMode === 'default' && org?.defaultRole && (
                  <>
                    <p className='mb-6 font-semibold'>Default role: {org?.defaultRole?.name}</p>
                    <ReadOnlyRoleOverview capabilities={org?.defaultRole?.capabilities} />
                  </>
                )}
                {invitePermissionsMode === 'custom' && (
                  <>
                    <div className={selectedRoleTemplate?.name === CUSTOM_ROLE_TEMPLATE_NAME ? '' : 'hidden'}>
                      <CustomRoleBuilder form={form} />
                    </div>
                    {selectedRoleTemplate?.name && selectedRoleTemplate?.name !== CUSTOM_ROLE_TEMPLATE_NAME && (
                      <div className=''>
                        <p className='mb-6 font-semibold'>{selectedRoleTemplate.name} permissions</p>
                        <ReadOnlyRoleOverview capabilities={selectedRoleTemplate.capabilities!} />
                      </div>
                    )}
                  </>
                )}
              </div>
            </aside>
            <footer className='border-neutral-3 flex items-center justify-end gap-2 border-t p-6'>
              <Button kind='secondary' onClick={close}>
                Cancel
              </Button>
              <form.Subscribe selector={(state) => [state.isSubmitting]}>
                {([isSubmitting]) => (
                  <Button
                    type='submit'
                    kind='primary'
                    disabled={isSubmitting}
                    icon={isSubmitting ? 'refresh' : null}
                    iconProps={{
                      isSpinning: isSubmitting,
                      size: 15,
                    }}
                  >
                    Send invite
                  </Button>
                )}
              </form.Subscribe>
            </footer>
          </div>
        </Form>
      </Drawer>
    </>
  )
}

export default OrgInvite
